import type {
  AppliesTo,
  CreateTax,
  Tax,
  TaxType,
  UpdateTax,
} from '@monorepo/shared/schemas/taxes'
import {
  APPLIES_TO_OPTIONS,
  TAX_TYPE,
  TAX_TYPES,
} from '@monorepo/shared/schemas/taxes'
import { DollarSign, Loader2, Percent, Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Switch } from '@/shared/components/ui/switch'
import { Textarea } from '@/shared/components/ui/textarea'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useCreateTax, useUpdateTax } from '../hooks/useTaxes'

interface TaxDrawerProps {
  tax?: Tax
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TaxDrawer({ tax, open, onOpenChange }: TaxDrawerProps) {
  const [formData, setFormData] = useState<Partial<CreateTax | UpdateTax>>({
    name: '',
    code: '',
    description: '',
    tax_type: TAX_TYPE.percentage,
    percentage_value: 10,
    fixed_amount_cents: undefined,
    currency: 'USD',
    applies_to: undefined,
    taxable_amount_type: undefined,
    compound: false,
    jurisdiction: '',
    jurisdiction_level: undefined,
    tax_authority: '',
    auto_apply: false,
    is_active: true,
    metadata: {},
  })

  const updateTax = useUpdateTax()
  const createTax = useCreateTax()

  // Update form data when tax changes or reset when creating
  useEffect(() => {
    if (tax) {
      // Edit mode: populate with tax data
      setFormData({
        name: tax.name,
        code: tax.code,
        description: tax.description || '',
        tax_type: tax.tax_type,
        percentage_value: tax.percentage_value || undefined,
        fixed_amount_cents: tax.fixed_amount_cents || undefined,
        currency: tax.currency || 'USD',
        applies_to: tax.applies_to || undefined,
        taxable_amount_type: tax.taxable_amount_type || undefined,
        compound: tax.compound,
        jurisdiction: tax.jurisdiction || '',
        jurisdiction_level: tax.jurisdiction_level || undefined,
        tax_authority: tax.tax_authority || '',
        auto_apply: tax.auto_apply,
        is_active: tax.is_active,
        metadata: tax.metadata || {},
      })
    } else {
      // Create mode: reset to empty form
      setFormData({
        name: '',
        code: '',
        description: '',
        tax_type: TAX_TYPE.percentage,
        percentage_value: 10,
        fixed_amount_cents: undefined,
        currency: 'USD',
        applies_to: undefined,
        taxable_amount_type: undefined,
        compound: false,
        jurisdiction: '',
        jurisdiction_level: undefined,
        tax_authority: '',
        auto_apply: false,
        is_active: true,
        metadata: {},
      })
    }
  }, [tax])

  const handleSave = async () => {
    try {
      // Prepare data based on tax_type
      const preparedData: Record<string, unknown> = {
        name: formData.name!,
        code: formData.code!.toUpperCase(),
        description: formData.description || undefined,
        tax_type: formData.tax_type!,
        applies_to: formData.applies_to || undefined,
        taxable_amount_type: formData.taxable_amount_type || undefined,
        compound: formData.compound || false,
        jurisdiction: formData.jurisdiction || undefined,
        jurisdiction_level: formData.jurisdiction_level || undefined,
        tax_authority: formData.tax_authority || undefined,
        auto_apply: formData.auto_apply || false,
        is_active: formData.is_active ?? true,
        metadata: formData.metadata || {},
      }

      // Add type-specific fields
      if (
        formData.tax_type === TAX_TYPE.percentage ||
        formData.tax_type === TAX_TYPE.compound
      ) {
        preparedData.percentage_value = formData.percentage_value
        preparedData.fixed_amount_cents = undefined
        preparedData.currency = undefined
      } else if (formData.tax_type === TAX_TYPE.fixed) {
        preparedData.fixed_amount_cents = formData.fixed_amount_cents
        preparedData.currency = formData.currency
        preparedData.percentage_value = undefined
      }

      if (tax) {
        // Edit mode: update existing tax
        await updateTax.mutateAsync({
          id: tax.id,
          data: preparedData,
        })
        onOpenChange(false)
      } else {
        // Create mode: create new tax
        await createTax.mutateAsync(preparedData as never)
        onOpenChange(false)
      }
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const isValid = () => {
    if (!formData.name?.trim() || !formData.code?.trim()) return false
    if (
      (formData.tax_type === TAX_TYPE.percentage ||
        formData.tax_type === TAX_TYPE.compound) &&
      !formData.percentage_value
    )
      return false
    if (
      formData.tax_type === TAX_TYPE.fixed &&
      (!formData.fixed_amount_cents || !formData.currency)
    )
      return false
    return true
  }

  const isLoading = updateTax.isPending || createTax.isPending

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
        <DrawerHeader>
          <DrawerTitle>{tax ? 'Edit Tax' : 'Create New Tax'}</DrawerTitle>
          <DrawerDescription>
            {tax
              ? 'Update the tax details and rates.'
              : 'Create a new tax configuration for your products and services.'}
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-4'>
          <div className='space-y-4'>
            {/* Tax Name */}
            <div>
              <Label htmlFor='tax-name'>Tax Name *</Label>
              <Input
                id='tax-name'
                value={formData.name}
                onChange={e =>
                  setFormData(prev => ({ ...prev, name: e.target.value }))
                }
                placeholder='e.g., Value Added Tax'
                className='mt-1'
              />
            </div>

            {/* Tax Code */}
            <div>
              <Label htmlFor='tax-code'>Tax Code *</Label>
              <Input
                id='tax-code'
                value={formData.code}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    code: e.target.value.toUpperCase(),
                  }))
                }
                placeholder='e.g., VAT-20'
                className='mt-1 font-mono'
                disabled={!!tax} // Can't change code in edit mode
              />
              <p className='text-xs text-slate-500 mt-1'>
                {tax
                  ? 'Code cannot be changed after creation'
                  : 'Unique identifier for this tax'}
              </p>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor='description'>Description</Label>
              <Textarea
                id='description'
                value={formData.description || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder='Describe this tax'
                rows={2}
                className='mt-1'
              />
            </div>

            {/* Tax Type */}
            <div>
              <Label htmlFor='tax-type'>Tax Type *</Label>
              <Select
                value={formData.tax_type}
                onValueChange={(value: TaxType) => {
                  setFormData(prev => ({
                    ...prev,
                    tax_type: value,
                    // Reset type-specific fields
                    percentage_value: value !== TAX_TYPE.fixed ? 10 : undefined,
                    fixed_amount_cents:
                      value === TAX_TYPE.fixed ? 100 : undefined,
                    currency: value === TAX_TYPE.fixed ? 'USD' : undefined,
                  }))
                }}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAX_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      <div className='flex items-center gap-2'>
                        {type === TAX_TYPE.fixed ? (
                          <DollarSign className='w-4 h-4' />
                        ) : (
                          <Percent className='w-4 h-4' />
                        )}
                        <span className='capitalize'>{type}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Conditional: Percentage Value */}
            {(formData.tax_type === TAX_TYPE.percentage ||
              formData.tax_type === TAX_TYPE.compound) && (
              <div>
                <Label htmlFor='percentage-value'>Tax Percentage *</Label>
                <Input
                  id='percentage-value'
                  type='number'
                  min='0'
                  max='100'
                  step='0.01'
                  value={formData.percentage_value || ''}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      percentage_value: parseFloat(e.target.value),
                    }))
                  }
                  placeholder='20'
                  className='mt-1'
                />
                <p className='text-xs text-slate-500 mt-1'>
                  Percentage rate (0-100)
                </p>
              </div>
            )}

            {/* Conditional: Fixed Amount Fields */}
            {formData.tax_type === TAX_TYPE.fixed && (
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <Label htmlFor='currency'>Currency *</Label>
                  <Select
                    value={formData.currency || 'USD'}
                    onValueChange={value =>
                      setFormData(prev => ({ ...prev, currency: value }))
                    }
                  >
                    <SelectTrigger className='mt-1'>
                      <SelectValue placeholder='Select currency' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='USD'>USD</SelectItem>
                      <SelectItem value='EUR'>EUR</SelectItem>
                      <SelectItem value='GBP'>GBP</SelectItem>
                      <SelectItem value='CAD'>CAD</SelectItem>
                      <SelectItem value='AUD'>AUD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor='amount'>Fixed Tax Amount *</Label>
                  <Input
                    id='amount'
                    type='number'
                    min='0'
                    step='0.01'
                    value={
                      formData.fixed_amount_cents
                        ? (formData.fixed_amount_cents / 100).toFixed(2)
                        : ''
                    }
                    onChange={e => {
                      const value = e.target.value
                      if (value === '' || value === null) {
                        setFormData(prev => ({
                          ...prev,
                          fixed_amount_cents: undefined,
                        }))
                      } else {
                        const numValue = parseFloat(value)
                        if (!Number.isNaN(numValue)) {
                          setFormData(prev => ({
                            ...prev,
                            fixed_amount_cents: Math.round(numValue * 100),
                          }))
                        }
                      }
                    }}
                    placeholder='5.00'
                    className='mt-1'
                  />
                </div>
              </div>
            )}

            {/* Applies To */}
            <div>
              <Label htmlFor='applies-to'>Applies To (Optional)</Label>
              <Select
                value={formData.applies_to || undefined}
                onValueChange={(value: AppliesTo) =>
                  setFormData(prev => ({ ...prev, applies_to: value }))
                }
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder='Select what this tax applies to' />
                </SelectTrigger>
                <SelectContent>
                  {APPLIES_TO_OPTIONS.map(option => (
                    <SelectItem key={option} value={option}>
                      <span className='capitalize'>
                        {option.replace('_', ' ')}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Jurisdiction */}
            <div>
              <Label htmlFor='jurisdiction'>Jurisdiction</Label>
              <Input
                id='jurisdiction'
                value={formData.jurisdiction || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    jurisdiction: e.target.value,
                  }))
                }
                placeholder='e.g., California, United Kingdom'
                className='mt-1'
              />
            </div>

            {/* Tax Authority */}
            <div>
              <Label htmlFor='tax-authority'>Tax Authority</Label>
              <Input
                id='tax-authority'
                value={formData.tax_authority || ''}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    tax_authority: e.target.value,
                  }))
                }
                placeholder='e.g., California Department of Tax'
                className='mt-1'
              />
            </div>

            {/* Settings */}
            <div className='space-y-4 pt-2 border-t'>
              <div className='flex items-center justify-between'>
                <div>
                  <Label htmlFor='compound'>Compound Tax</Label>
                  <p className='text-xs text-muted-foreground'>
                    Calculate tax on top of other taxes
                  </p>
                </div>
                <Switch
                  id='compound'
                  checked={formData.compound || false}
                  onCheckedChange={checked =>
                    setFormData(prev => ({ ...prev, compound: checked }))
                  }
                />
              </div>

              <div className='flex items-center justify-between'>
                <div>
                  <Label htmlFor='auto-apply'>Auto Apply</Label>
                  <p className='text-xs text-muted-foreground'>
                    Automatically apply to applicable items
                  </p>
                </div>
                <Switch
                  id='auto-apply'
                  checked={formData.auto_apply || false}
                  onCheckedChange={checked =>
                    setFormData(prev => ({ ...prev, auto_apply: checked }))
                  }
                />
              </div>

              <div className='flex items-center justify-between'>
                <div>
                  <Label htmlFor='is-active'>Active</Label>
                  <p className='text-xs text-muted-foreground'>
                    Make this tax available for use
                  </p>
                </div>
                <Switch
                  id='is-active'
                  checked={formData.is_active ?? true}
                  onCheckedChange={checked =>
                    setFormData(prev => ({ ...prev, is_active: checked }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <DrawerFooter className='border-t'>
          <div className='flex gap-2 justify-end w-full'>
            <Button
              variant='outline'
              onClick={handleCancel}
              disabled={isLoading}
            >
              <X className='w-4 h-4 mr-2' />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!isValid() || isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Saving...
                </>
              ) : (
                <>
                  <Save className='w-4 h-4 mr-2' />
                  {tax ? 'Update Tax' : 'Create Tax'}
                </>
              )}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
